import { Box, Code, Flex, Heading } from '@radix-ui/themes';
import * as Sentry from '@sentry/react';
import { CommonDetails } from 'components/common/details';
import { IIdentifiedComponent } from 'interfaces/i-components';
import { RADIX } from 'lib_ts/enums/radix-ui';
import React, { PropsWithChildren } from 'react';

interface IProps extends IIdentifiedComponent {
  children: React.ReactNode;
  hideIntercom?: boolean;
  error?: Error;
}

// shown when the boundary is triggered
const ErrorFallback = (props: {
  identifier: string;
  hideIntercom?: boolean;
  error?: Error;
}) => {
  const { identifier, hideIntercom, error } = props;

  return (
    <Flex p={RADIX.FLEX.PAD.LG} direction="column" gap={RADIX.FLEX.GAP.LG}>
      <Heading size={RADIX.HEADING.SIZE.SM}>
        Uh-oh, something broke in {identifier}
      </Heading>

      <Box>
        It seems something isn't working quite right, but we can fix that.
      </Box>

      {hideIntercom ? (
        <Box>
          If this is the first time you've encountered this issue, please
          contact support and we'll be in touch shortly!
        </Box>
      ) : (
        <Box>
          If this is the first time you've encountered this issue, please use
          the Intercom button to open a support ticket and we'll be in touch
          shortly!
        </Box>
      )}

      {error?.message && (
        <CommonDetails
          summaryColor={RADIX.COLOR.DANGER}
          summary="Error Message"
          defaultOpen
        >
          <Code color={RADIX.COLOR.DANGER}>{error.message}</Code>
        </CommonDetails>
      )}
    </Flex>
  );
};

export const ErrorBoundary = (props: PropsWithChildren<IProps>) => {
  const { identifier, hideIntercom, children } = props;

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag('component', identifier);
        scope.setExtra('ErrorBoundary.identifier', identifier);
      }}
      onError={(error, componentStack) => {
        const errorMessage =
          error instanceof Error ? error.message : String(error);

        console.error(`${identifier}: ${errorMessage}`, componentStack);
      }}
      fallback={({ error }) => (
        <ErrorFallback
          identifier={identifier}
          hideIntercom={hideIntercom}
          error={error as Error | undefined}
        />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
